import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useSettings } from '@/context/SettingsContext';
import { PosReceipt } from '@/components/PosReceipt';
import { buildReceiptXml, buildKotXml, epsonPrint, usesEpsonApp, noAppToastArgs } from '@/lib/eposPrint';
import usePermissions from '@/hooks/usePermissions';
import { Store, Search, Plus, Minus, Trash2, Printer, PauseCircle, PlayCircle, Settings2, Power, Undo2, UtensilsCrossed } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const errMsg = (e, fallback = 'Failed') => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x?.msg || 'Invalid input').join(', ');
  return typeof d === 'string' ? d : fallback;
};


export default function POSPage() {
  const { formatCurrency, currency } = useSettings();
  const { isAdmin, hasPermission } = usePermissions();
  const canManage = isAdmin || hasPermission('pos.manage');
  const canDiscount = isAdmin || hasPermission('pos.discount');

  const [stores, setStores] = useState([]);
  const [registerId, setRegisterId] = useState(localStorage.getItem('pos_register') || '');
  const [shift, setShift] = useState(null);
  const [shiftTotals, setShiftTotals] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [cart, setCart] = useState([]);
  const [held, setHeld] = useState(JSON.parse(localStorage.getItem('pos_held') || '[]'));
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutDraft, setCheckoutDraft] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [showShift, setShowShift] = useState(false);
  const [shiftTx, setShiftTx] = useState(null);
  const [openingFloat, setOpeningFloat] = useState('');
  const [countedCash, setCountedCash] = useState('');
  const [handoverCash, setHandoverCash] = useState('');
  const [varianceReason, setVarianceReason] = useState('');
  const [terminalBatch, setTerminalBatch] = useState('');
  const [billDiscount, setBillDiscount] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [showReturns, setShowReturns] = useState(false);
  const [tableBills, setTableBills] = useState([]);
  const [orderType, setOrderType] = useState('retail');
  const [tableNumber, setTableNumber] = useState('');
  const [tableBillId, setTableBillId] = useState(null);
  const searchRef = useRef(null);

  const register = stores.flatMap(s => (s.registers || []).map(r => ({ ...r, store: s }))).find(r => r.id === registerId);
  const industry = register?.store?.industry || 'retail';
  const isRestaurant = industry === 'restaurant';
  const canRefund = isAdmin || hasPermission('pos.refund');

  const loadStores = useCallback(async () => {
    try { const r = await axios.get(`${API}/pos/stores`, auth()); setStores(r.data.stores || []); } catch { /* no perm */ }
  }, []);
  const loadShift = useCallback(async () => {
    if (!registerId) return;
    try {
      const r = await axios.get(`${API}/pos/shifts/current?register_id=${registerId}`, auth());
      setShift(r.data.shift); setShiftTotals(r.data.totals || null);
    } catch { setShift(null); }
  }, [registerId]);
  const loadProducts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/pos/products?limit=100${q ? `&q=${encodeURIComponent(q)}` : ''}${cat ? `&category=${encodeURIComponent(cat)}` : ''}`, auth());
      setProducts(r.data.products || []); setCategories(r.data.categories || []);
    } catch { /* noop */ }
  }, [q, cat]);

  useEffect(() => { loadStores(); }, [loadStores]);
  useEffect(() => { loadShift(); }, [loadShift]);
  useEffect(() => {
    if (showShift && shift) {
      axios.get(`${API}/pos/shifts/${shift.id}/transactions`, auth())
        .then((r) => setShiftTx(r.data)).catch(() => setShiftTx(null));
    }
  }, [showShift, shift]);
  useEffect(() => { const t = setTimeout(loadProducts, 250); return () => clearTimeout(t); }, [loadProducts]);

  const loadTables = useCallback(async () => {
    if (!register?.store?.id || !isRestaurant) { setTableBills([]); return; }
    try {
      const r = await axios.get(`${API}/pos/table-bills?store_id=${register.store.id}`, auth());
      setTableBills(r.data.bills || []);
    } catch { setTableBills([]); }
  }, [register?.store?.id, isRestaurant]);
  useEffect(() => { loadTables(); }, [loadTables]);
  useEffect(() => { if (isRestaurant) setOrderType('dine_in'); else setOrderType('retail'); }, [isRestaurant]);

  const addToCart = (p) => {
    // Exact-SKU scan: hitting Enter in search with a full SKU adds directly
    setCart(c => {
      const i = c.findIndex(x => x.sku === p.sku);
      if (i >= 0) { const n = [...c]; n[i] = { ...n[i], quantity: n[i].quantity + 1 }; return n; }
      return [...c, { sku: p.sku, name: p.name || p.sku, price: p.price, quantity: 1, discount: 0, note: '' }];
    });
    if (industry === 'grocery') { setQ(''); searchRef.current?.focus(); }
  };
  const setQty = (sku, d) => setCart(c => c.map(x => x.sku === sku ? { ...x, quantity: Math.max(1, x.quantity + d) } : x));
  const removeLine = (sku) => setCart(c => c.filter(x => x.sku !== sku));
  const onSearchEnter = (e) => {
    if (e.key !== 'Enter') return;
    const exact = products.find(p => p.sku?.toLowerCase() === q.trim().toLowerCase());
    if (exact) { addToCart(exact); setQ(''); searchRef.current?.focus(); }
  };

  const subtotal = cart.reduce((s, x) => s + x.price * x.quantity, 0);
  const lineDisc = cart.reduce((s, x) => s + (x.discount || 0), 0);
  const total = Math.max(0, subtotal - lineDisc - (parseFloat(billDiscount) || 0));

  const holdCart = () => {
    if (!cart.length) return;
    const h = [...held, { id: Date.now(), at: new Date().toISOString(), cart, billDiscount }];
    setHeld(h); localStorage.setItem('pos_held', JSON.stringify(h));
    setCart([]); setBillDiscount(''); toast.success('Bill parked');
  };
  const recall = (h) => {
    setCart(h.cart); setBillDiscount(h.billDiscount || '');
    const rest = held.filter(x => x.id !== h.id);
    setHeld(rest); localStorage.setItem('pos_held', JSON.stringify(rest));
  };

  const saveToTable = async () => {
    if (!cart.length || !tableNumber.trim()) { toast.error('Table number and items required'); return; }
    try {
      const r = await axios.post(`${API}/pos/table-bills`, {
        id: tableBillId, store_id: register.store.id, register_id: registerId,
        table_number: tableNumber, order_type: orderType, items: cart,
        bill_discount: parseFloat(billDiscount) || 0,
      }, auth());
      toast.success(`Saved to table ${r.data.table_number}`);
      setCart([]); setBillDiscount(''); setTableNumber(''); setTableBillId(null);
      loadTables();
    } catch (e) { toast.error(errMsg(e, 'Failed')); }
  };
  const openTableBill = (b) => {
    setCart(b.items || []); setBillDiscount(String(b.bill_discount || '')); 
    setTableNumber(b.table_number); setTableBillId(b.id); setOrderType(b.order_type || 'dine_in');
  };

  const [printMode, setPrintMode] = useState('receipt');
  const printReceipt = () => {
    if (usesEpsonApp(receipt?.store)) { epsonPrint(buildReceiptXml(receipt), () => toast.error(...noAppToastArgs())); return; }
    setPrintMode('receipt'); setTimeout(() => window.print(), 150);
  };
  const printKot = () => {
    if (usesEpsonApp(receipt?.store)) { epsonPrint(buildKotXml(receipt), () => toast.error(...noAppToastArgs())); return; }
    setPrintMode('kot'); setTimeout(() => window.print(), 150);
  };

  const openShift = async () => {
    try {
      await axios.post(`${API}/pos/shifts/open`, { register_id: registerId, opening_float: parseFloat(openingFloat) || 0 }, auth());
      toast.success('Shift opened'); setShowShift(false); setOpeningFloat(''); loadShift();
    } catch (e) { toast.error(errMsg(e, 'Failed')); }
  };
  const closeShift = async () => {
    if (countedCash === '') { toast.error('Count the drawer and enter the counted cash'); return; }
    try {
      const r = await axios.post(`${API}/pos/shifts/${shift.id}/close`, {
        counted_cash: parseFloat(countedCash),
        handover_cash: handoverCash === '' ? null : parseFloat(handoverCash),
        variance_reason: varianceReason.trim(),
        terminal_batch_total: terminalBatch === '' ? null : parseFloat(terminalBatch),
      }, auth());
      toast.success(`Shift closed — counted ${formatCurrency(r.data.counted_cash)}${Math.abs(r.data.difference) > 0.01 ? `, difference ${formatCurrency(r.data.difference)}` : ''}`);
      if (r.data.card_difference != null && Math.abs(r.data.card_difference) > 0.01) toast.warning(`Card terminal batch off by ${formatCurrency(r.data.card_difference)} — admins notified`);
      if (r.data.handover_cash > 0) toast.success(`${formatCurrency(r.data.handover_cash)} handover recorded — pending admin confirmation in Cash Management`);
      setShowShift(false); setCountedCash(''); setHandoverCash(''); setVarianceReason(''); setTerminalBatch(''); loadShift();
    } catch (e) { toast.error(errMsg(e, 'Failed')); }
  };

  return (
    <div className="min-h-screen bg-wt-bg" data-testid="pos-page">
      {/* Print-only receipt */}
      <style>{`@media print { body * { visibility: hidden; } #pos-receipt, #pos-receipt * { visibility: visible; } #pos-receipt { position: absolute; left: 0; top: 0; width: ${receipt?.store?.paper_width === '58' ? '58mm' : '80mm'}; font-family: monospace; color: #000; background: #fff; } }`}</style>
      {receipt && printMode === 'kot' && (
        <div id="pos-receipt" className="hidden print:block p-2 text-[12px] leading-tight">
          <div className="text-center font-bold text-[14px]">*** KITCHEN ***</div>
          <div className="text-center">{receipt.order.pos?.order_type === 'takeaway' ? 'TAKEAWAY' : receipt.order.pos?.order_type === 'delivery' ? 'DELIVERY' : `TABLE ${receipt.order.pos?.table_number || '-'}`}</div>
          <div>{receipt.order.order_number} · {(receipt.order.created_at || '').slice(11, 16)}</div>
          <div>--------------------------------</div>
          {receipt.order.items.map((i, idx) => (
            <div key={idx}>
              <div className="font-bold">{i.quantity} x {i.name}</div>
              {i.note && <div>&nbsp;&nbsp;&gt;&gt; {i.note}</div>}
            </div>
          ))}
          <div>--------------------------------</div>
          <div>Staff: {receipt.order.pos?.staff_name}</div>
        </div>
      )}
      {receipt && printMode === 'receipt' && <PosReceipt receipt={receipt} withStyle={false} />}

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-wt-border bg-wt-paper sticky top-0 z-10 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Store className="w-5 h-5 text-wt-gold" />
          <select value={registerId} onChange={(e) => { setRegisterId(e.target.value); localStorage.setItem('pos_register', e.target.value); }}
            className="bg-wt-bg border border-wt-border rounded px-2 py-1.5 text-sm text-wt-text-primary" data-testid="pos-register-select">
            <option value="">Select register…</option>
            {stores.map(s => (s.registers || []).map(r => <option key={r.id} value={r.id}>{s.name} — {r.name}</option>))}
          </select>
          {shift ? <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/40" data-testid="shift-open-badge">Shift open · {shiftTotals ? formatCurrency(shiftTotals.total_sales) : ''}</Badge>
            : registerId && <Badge className="bg-red-500/20 text-red-400 border-red-500/40">Shift closed</Badge>}
        </div>
        <div className="flex gap-2">
          {canRefund && (
            <Button size="sm" variant="outline" className="border-wt-border" onClick={() => setShowReturns(true)} data-testid="pos-returns-btn">
              <Undo2 className="w-4 h-4 mr-1" />Returns
            </Button>
          )}
          {held.length > 0 && held.map(h => (
            <Button key={h.id} size="sm" variant="outline" className="border-amber-500/40 text-amber-400" onClick={() => recall(h)} data-testid={`recall-${h.id}`}>
              <PlayCircle className="w-4 h-4 mr-1" />{h.cart.length} items
            </Button>
          ))}
          {registerId && (
            <Button size="sm" variant="outline" className="border-wt-border" onClick={() => setShowShift(true)} data-testid="shift-btn">
              <Power className="w-4 h-4 mr-1" />{shift ? 'Close Shift' : 'Open Shift'}
            </Button>
          )}
          {canManage && (
            <Button size="sm" variant="outline" className="border-wt-border" onClick={() => window.location.assign('/pos/setup')} data-testid="pos-setup-btn">
              <Settings2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Restaurant: open tables bar */}
      {isRestaurant && tableBills.length > 0 && (
        <div className="flex gap-2 px-3 pt-2 overflow-x-auto" data-testid="pos-tables-bar">
          {tableBills.map(b => (
            <button key={b.id} onClick={() => openTableBill(b)}
              className={`px-3 py-2 rounded-lg border text-xs whitespace-nowrap ${tableBillId === b.id ? 'border-wt-gold bg-wt-gold/10 text-wt-gold' : 'border-amber-500/40 bg-wt-paper text-amber-400'}`}
              data-testid={`table-bill-${b.table_number}`}>
              <UtensilsCrossed className="w-3 h-3 inline mr-1" />Table {b.table_number} · {(b.items || []).length} items
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-3 p-3">
        {/* Products */}
        <div className="flex-1 min-w-0">
          <div className="flex gap-2 mb-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-3 text-wt-text-muted" />
              <Input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onSearchEnter}
                placeholder="Search or scan SKU / barcode…" className="pl-9 h-11 text-base" data-testid="pos-search" />
            </div>
          </div>
          <div className="flex gap-1.5 mb-2 overflow-x-auto pb-1">
            <button onClick={() => setCat('')} className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${!cat ? 'bg-wt-gold text-black' : 'bg-wt-paper border border-wt-border text-wt-text-muted'}`}>All</button>
            {categories.map(c => (
              <button key={c} onClick={() => setCat(c)} className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${cat === c ? 'bg-wt-gold text-black' : 'bg-wt-paper border border-wt-border text-wt-text-muted'}`}>{c}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2" data-testid="pos-product-grid">
            {products.map(p => (
              <button key={p.sku} onClick={() => addToCart(p)} disabled={!shift}
                className="bg-wt-paper border border-wt-border rounded-lg p-2 text-left active:scale-95 transition-transform disabled:opacity-40"
                data-testid={`pos-product-${p.sku}`}>
                {p.image_url ? <img src={p.image_url} alt="" className="w-full h-20 object-cover rounded mb-1" /> : <div className="w-full h-20 bg-wt-bg rounded mb-1" />}
                <p className="text-xs text-wt-text-primary line-clamp-2 min-h-[2rem]">{p.name}</p>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-wt-gold">{formatCurrency(p.price)}</span>
                  <span className={`text-[10px] ${p.available > 0 ? 'text-wt-text-muted' : 'text-red-400'}`}>{p.available} in stock</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Cart */}
        <div className="w-full lg:w-[380px] shrink-0 bg-wt-paper border border-wt-border rounded-xl p-3 flex flex-col" data-testid="pos-cart">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold text-wt-text-primary">Cart ({cart.length})</h2>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={holdCart} disabled={!cart.length} title="Park bill" data-testid="hold-btn"><PauseCircle className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => { setCart([]); setBillDiscount(''); setTableBillId(null); setTableNumber(''); }} disabled={!cart.length} data-testid="clear-cart-btn"><Trash2 className="w-4 h-4 text-red-400" /></Button>
            </div>
          </div>
          {isRestaurant && (
            <div className="mb-2 space-y-2" data-testid="restaurant-controls">
              <div className="grid grid-cols-3 gap-1.5">
                {[['dine_in', 'Dine-in'], ['takeaway', 'Takeaway'], ['delivery', 'Delivery']].map(([v, l]) => (
                  <button key={v} onClick={() => setOrderType(v)} data-testid={`order-type-${v}`}
                    className={`py-2 rounded-lg border text-xs ${orderType === v ? 'border-wt-gold bg-wt-gold/10 text-wt-gold' : 'border-wt-border text-wt-text-muted'}`}>{l}</button>
                ))}
              </div>
              {orderType === 'dine_in' && (
                <div className="flex gap-2">
                  <Input placeholder="Table #" value={tableNumber} onChange={(e) => setTableNumber(e.target.value)} className="h-9" data-testid="table-number-input" />
                  <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-400 h-9 whitespace-nowrap"
                    onClick={saveToTable} disabled={!cart.length || !tableNumber.trim()} data-testid="save-to-table-btn">
                    <UtensilsCrossed className="w-3.5 h-3.5 mr-1" />{tableBillId ? 'Update table' : 'Save to table'}
                  </Button>
                </div>
              )}
            </div>
          )}
          <div className="flex-1 overflow-auto space-y-2 min-h-[160px] max-h-[45vh]">
            {cart.map(x => (
              <div key={x.sku} className="bg-wt-bg/50 rounded-lg p-2" data-testid={`cart-line-${x.sku}`}>
                <div className="flex justify-between text-xs text-wt-text-primary"><span className="line-clamp-1">{x.name}</span><span>{formatCurrency(x.price * x.quantity - (x.discount || 0))}</span></div>
                <div className="flex items-center justify-between mt-1">
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="outline" className="h-8 w-8 border-wt-border" onClick={() => setQty(x.sku, -1)} data-testid={`qty-minus-${x.sku}`}><Minus className="w-3 h-3" /></Button>
                    <span className="w-8 text-center text-sm text-wt-text-primary">{x.quantity}</span>
                    <Button size="icon" variant="outline" className="h-8 w-8 border-wt-border" onClick={() => setQty(x.sku, 1)} data-testid={`qty-plus-${x.sku}`}><Plus className="w-3 h-3" /></Button>
                  </div>
                  <div className="flex items-center gap-1">
                    {canDiscount && (
                      <Input type="number" placeholder="disc" value={x.discount || ''} className="h-8 w-16 text-xs"
                        onChange={(e) => setCart(c => c.map(y => y.sku === x.sku ? { ...y, discount: parseFloat(e.target.value) || 0 } : y))} />
                    )}
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-400" onClick={() => removeLine(x.sku)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
                {isRestaurant && (
                  <Input placeholder="Note (e.g. no onions)" value={x.note || ''} className="h-7 mt-1 text-xs"
                    onChange={(e) => setCart(c => c.map(y => y.sku === x.sku ? { ...y, note: e.target.value } : y))}
                    data-testid={`item-note-${x.sku}`} />
                )}
              </div>
            ))}
            {cart.length === 0 && <p className="text-center text-wt-text-muted text-sm py-8">Tap products to add</p>}
          </div>
          <div className="border-t border-wt-border/50 pt-2 mt-2 space-y-1 text-sm">
            <div className="flex justify-between text-wt-text-muted"><span>Subtotal</span><span>{formatCurrency(subtotal)}</span></div>
            {canDiscount && (
              <div className="flex justify-between items-center text-wt-text-muted">
                <span>Bill discount</span>
                <Input type="number" value={billDiscount} onChange={(e) => setBillDiscount(e.target.value)} className="h-8 w-24 text-right" data-testid="bill-discount-input" />
              </div>
            )}
            <div className="flex justify-between text-lg font-bold text-wt-text-primary"><span>Total</span><span className="text-wt-gold" data-testid="cart-total">{formatCurrency(total)}</span></div>
            <Button className="w-full h-12 text-base mt-1" disabled={!cart.length || !shift} onClick={() => setShowCheckout(true)} data-testid="checkout-btn">
              Charge {formatCurrency(total)}
            </Button>
            {!shift && registerId && <p className="text-[11px] text-amber-400 text-center">Open a shift to start selling</p>}
          </div>
        </div>
      </div>

      {showCheckout && (
        <CheckoutDialog total={total} cart={cart} billDiscount={parseFloat(billDiscount) || 0} registerId={registerId}
          store={register?.store}
          formatCurrency={formatCurrency} isRestaurant={isRestaurant} orderType={orderType}
          tableNumber={tableNumber} tableBillId={tableBillId}
          draft={checkoutDraft} onDraftChange={setCheckoutDraft}
          onDone={async (order) => {
            setShowCheckout(false); setCart([]); setBillDiscount(''); setTableBillId(null); setTableNumber('');
            setCheckoutDraft(null);
            loadTables();
            try { const r = await axios.get(`${API}/pos/sales/${order.id}/receipt`, auth()); setReceipt({ ...r.data, currency }); } catch { /* noop */ }
            loadShift();
          }}
          onClose={() => setShowCheckout(false)} />
      )}

      {/* Receipt ready bar */}
      {receipt && (
        <div className="fixed bottom-3 left-1/2 -translate-x-1/2 bg-wt-paper border border-wt-gold/50 rounded-full px-4 py-2 flex items-center gap-3 z-20 print:hidden" data-testid="receipt-bar">
          <span className="text-sm text-wt-text-primary">{receipt.order.order_number} · {formatCurrency(receipt.order.total_amount)}</span>
          <Button size="sm" onClick={printReceipt} data-testid="print-receipt-btn"><Printer className="w-4 h-4 mr-1" />Print</Button>
          {isRestaurant && <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-400" onClick={printKot} data-testid="print-kot-btn"><UtensilsCrossed className="w-4 h-4 mr-1" />KOT</Button>}
          <Button size="sm" variant="ghost" onClick={() => setReceipt(null)}>✕</Button>
        </div>
      )}

      {/* Shift dialog */}
      <Dialog open={showShift} onOpenChange={setShowShift}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-sm">
          <DialogHeader><DialogTitle className="text-wt-text-primary">{shift ? 'Close Shift' : 'Open Shift'}</DialogTitle></DialogHeader>
          {shift ? (
            <div className="space-y-2 text-sm text-wt-text-primary">
              {shiftTotals && (
                <>
                  <div className="flex justify-between"><span className="text-wt-text-muted">Sales</span><span>{shiftTotals.sales_count} · {formatCurrency(shiftTotals.total_sales)}</span></div>
                  <div className="flex justify-between"><span className="text-wt-text-muted">Cash / Card / Link</span><span>{formatCurrency(shiftTotals.cash_sales)} / {formatCurrency(shiftTotals.card_sales)} / {formatCurrency(shiftTotals.link_sales)}</span></div>
                  <div className="flex justify-between font-semibold"><span>Expected cash in drawer</span><span className="text-wt-gold">{formatCurrency(shiftTotals.expected_cash)}</span></div>
                </>
              )}
              {shiftTx && (
                <div className="border border-wt-border/50 rounded-lg p-2 space-y-1" data-testid="register-transactions">
                  <p className="text-xs font-semibold text-wt-text-primary">Register transactions ({(shiftTx.orders || []).length})</p>
                  {Object.entries(shiftTx.by_method || {}).map(([m, amt]) => (
                    <div key={m} className="flex justify-between text-xs"><span className="text-wt-text-muted capitalize">{m.replace('_', ' ')}</span><span>{formatCurrency(amt)}</span></div>
                  ))}
                  <div className="max-h-36 overflow-y-auto space-y-1 pt-1 border-t border-wt-border/40">
                    {(shiftTx.orders || []).map((o) => (
                      <div key={o.id} className="flex justify-between text-[11px]">
                        <span className="text-wt-text-muted">{o.order_number} · {(o.created_at || '').slice(11, 16)} · {(o.pos?.payments || []).map((p) => p.label || p.method).join('+') || '—'}</span>
                        <span>{formatCurrency(o.total_amount)}</span>
                      </div>
                    ))}
                    {(shiftTx.refunds || []).map((r) => (
                      <div key={r.id} className="flex justify-between text-[11px] text-red-400">
                        <span>Refund {r.order_number} ({r.method})</span><span>-{formatCurrency(r.refund_amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Label className="text-xs text-wt-text-muted">Counted cash * (count the drawer — required)</Label>
              <Input type="number" value={countedCash} onChange={(e) => setCountedCash(e.target.value)} placeholder="Enter actual counted amount" data-testid="counted-cash-input" />
              {countedCash !== '' && shiftTotals && Math.abs((parseFloat(countedCash) || 0) - shiftTotals.expected_cash) > 20 && (
                <>
                  <p className="text-[11px] text-red-400" data-testid="variance-warning">
                    Difference of {formatCurrency((parseFloat(countedCash) || 0) - shiftTotals.expected_cash)} vs expected — a reason is required, admins will be notified
                  </p>
                  <Input placeholder="Variance reason *" value={varianceReason} onChange={(e) => setVarianceReason(e.target.value)} data-testid="variance-reason-input" />
                </>
              )}
              {shiftTotals && (shiftTotals.card_sales_net ?? shiftTotals.card_sales) > 0 && (
                <>
                  <Label className="text-xs text-wt-text-muted">Card terminal batch total * (recorded card sales net of refunds: {formatCurrency(shiftTotals.card_sales_net ?? shiftTotals.card_sales)})</Label>
                  <Input type="number" value={terminalBatch} onChange={(e) => setTerminalBatch(e.target.value)} placeholder="Enter terminal settlement total" data-testid="terminal-batch-input" />
                </>
              )}
              <Label className="text-xs text-wt-text-muted">Cash to hand over</Label>
              <Input type="number" value={handoverCash} onChange={(e) => setHandoverCash(e.target.value)}
                placeholder={String(Math.max(0, (countedCash === '' ? (shiftTotals?.expected_cash || 0) : parseFloat(countedCash) || 0) - (shift.opening_float || 0)).toFixed(2))}
                data-testid="handover-cash-input" />
              <p className="text-[11px] text-wt-text-muted">Default keeps the opening float ({formatCurrency(shift.opening_float || 0)}) in the drawer. Handover cash goes to your wallet as <b>pending</b> — an admin confirms receipt in Cash Management before settlement.</p>
            </div>
          ) : (
            <div>
              <Label className="text-xs text-wt-text-muted">Opening cash float</Label>
              <Input type="number" value={openingFloat} onChange={(e) => setOpeningFloat(e.target.value)} className="mt-1" data-testid="opening-float-input" />
            </div>
          )}
          <DialogFooter>
            <Button onClick={shift ? closeShift : openShift}
              disabled={!!shift && (countedCash === ''
                || (!!shiftTotals && Math.abs((parseFloat(countedCash) || 0) - shiftTotals.expected_cash) > 20 && !varianceReason.trim())
                || (!!shiftTotals && (shiftTotals.card_sales_net ?? shiftTotals.card_sales) > 0 && terminalBatch === ''))}
              data-testid="shift-confirm-btn">{shift ? 'Close Shift' : 'Open Shift'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showSetup && <SetupDialog stores={stores} onRefresh={loadStores} onClose={() => { setShowSetup(false); loadStores(); }} />}
      {showReturns && <ReturnsDialog formatCurrency={formatCurrency} registerId={registerId} onClose={() => { setShowReturns(false); loadShift(); }} />}
    </div>
  );
}

const CheckoutDialog = ({ total, cart, billDiscount, registerId, store, formatCurrency, isRestaurant, orderType, tableNumber, tableBillId, draft, onDraftChange, onDone, onClose }) => {
  const [fulfillment, setFulfillment] = useState(draft?.fulfillment || (isRestaurant && orderType === 'delivery' ? 'delivery' : 'taken_now'));
  const [payments, setPayments] = useState(draft?.payments || []);
  const [cod, setCod] = useState(draft?.cod || false);
  const [customer, setCustomer] = useState(draft?.customer || null);
  const [addr, setAddr] = useState(draft?.addr || { first_name: '', last_name: '', company: '', address1: '', address2: '', city: '', province: '', country: 'United Arab Emirates', country_code: 'AE', zip: '', phone: '' });
  const [saving, setSaving] = useState(false);

  // Persist everything typed so closing the dialog / going back doesn't lose it
  useEffect(() => {
    onDraftChange?.({ fulfillment, payments, cod, customer, addr });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fulfillment, payments, cod, customer, addr]);
  const paid = payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const remaining = Math.max(0, total - paid);
  const change = Math.max(0, paid - total);
  const cardMissingRef = payments.some((p) => p.method === 'card' && (parseFloat(p.amount) || 0) > 0 && !(p.reference || '').trim());
  const hasLinkPayment = payments.some((p) => p.method === 'link' && (parseFloat(p.amount) || 0) > 0);
  const creditBal = parseFloat(customer?.store_credit_balance) || 0;
  const methods = [
    { key: 'cash', label: 'Cash', method: 'cash' },
    ...((store?.payment_methods?.length
      ? store.payment_methods.map((pm) => ({ key: pm.id || pm.name, label: pm.name, method: pm.type || 'card' }))
      : [{ key: 'card', label: 'Card', method: 'card' }, { key: 'link', label: 'Payment link', method: 'link' }])),
    ...(customer && creditBal > 0 ? [{ key: 'store_credit', label: `Store credit (${formatCurrency(creditBal)})`, method: 'store_credit' }] : []),
  ];
  const addPayment = (m) => {
    setCod(false);
    let amt = remaining > 0 ? remaining : 0;
    if (m.method === 'store_credit') {
      const used = payments.filter((p) => p.method === 'store_credit').reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
      amt = Math.min(amt, Math.max(0, creditBal - used));
      if (amt <= 0) { toast.error('No store credit left to apply'); return; }
    }
    setPayments((prev) => [...prev, { method: m.method, label: m.method === 'cash' || m.method === 'store_credit' ? '' : m.label, amount: amt.toFixed(2), reference: '' }]);
  };
  const setP = (i, k, v) => setPayments((prev) => prev.map((p, x) => (x === i ? { ...p, [k]: v } : p)));
  const setA = (k, v) => setAddr(a => ({ ...a, [k]: v }));

  useEffect(() => {
    if (!customer) return;
    setAddr(a => ({ ...a, first_name: a.first_name || customer.first_name || (customer.name || '').split(' ')[0] || '', last_name: a.last_name || customer.last_name || (customer.name || '').split(' ').slice(1).join(' ') || '', phone: a.phone || customer.phone || '' }));
    // Prefill saved address for returning customers (don't overwrite anything already typed)
    const da = customer.default_address;
    if (da && da.address1) {
      setAddr(a => a.address1.trim() ? a : ({
        ...a,
        company: a.company || da.company || '',
        address1: da.address1 || '',
        address2: a.address2 || da.address2 || '',
        city: a.city || da.city || '',
        province: a.province || da.province || '',
        country: da.country || a.country,
        country_code: da.country_code || a.country_code,
        zip: a.zip || da.zip || ''
      }));
    }
  }, [customer]);

  const deliveryInvalid = fulfillment === 'delivery' && (!addr.first_name.trim() || !addr.last_name.trim() || !addr.address1.trim() || !addr.city.trim() || !addr.phone.trim());

  const submit = async () => {
    setSaving(true);
    try {
      const payList = payments
        .map((p) => ({ method: p.method, label: p.label || '', amount: parseFloat(p.amount) || 0, reference: p.reference || '' }))
        .filter((p) => p.amount > 0);
      const body = {
        register_id: registerId,
        items: cart.map(x => ({ sku: x.sku, name: x.name || x.sku, quantity: x.quantity, price: x.price, discount: x.discount || 0, note: x.note || '' })),
        bill_discount: billDiscount,
        customer_id: customer?.id || null,
        customer_name: customer?.name || (fulfillment === 'delivery' ? `${addr.first_name} ${addr.last_name}`.trim() : ''),
        customer_phone: customer?.phone || addr.phone || '',
        customer_email: customer?.email || '',
        fulfillment,
        order_type: isRestaurant ? orderType : 'retail',
        table_number: tableNumber || '',
        table_bill_id: tableBillId || null,
        shipping_address: fulfillment === 'delivery' ? addr : {},
        payments: fulfillment === 'delivery' && cod ? [] : payList,
      };
      const r = await axios.post(`${API}/pos/sales`, body, auth());
      const sh = r.data.shopify;
      toast.success(`Sale ${r.data.order.order_number} completed${sh.synced ? ` — Shopify ${sh.order_name}` : ''}`);
      if (r.data.payment_url) {
        try { await navigator.clipboard.writeText(r.data.payment_url); toast.success('Payment link copied — share it with the customer. Order marks paid automatically on payment.'); }
        catch { toast.info(`Payment link (copy manually): ${r.data.payment_url}`, { duration: 15000 }); }
      }
      if (r.data.store_credit_warning) toast.warning(r.data.store_credit_warning);
      if (!sh.synced && sh.error) toast.warning(`Shopify sync failed: ${String(sh.error).slice(0, 80)} — retry from Shopify Sync Log`);
      onDone(r.data.order);
    } catch (e) { toast.error(errMsg(e, 'Sale failed')); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-wt-paper border-wt-border max-w-md" data-testid="checkout-dialog">
        <DialogHeader><DialogTitle className="text-wt-text-primary">Checkout — {formatCurrency(total)}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setFulfillment('taken_now')} data-testid="fulfillment-taken-now"
              className={`p-3 rounded-lg border text-sm ${fulfillment === 'taken_now' ? 'border-wt-gold bg-wt-gold/10 text-wt-gold' : 'border-wt-border text-wt-text-muted'}`}>
              🛍 Customer takes it now
            </button>
            <button onClick={() => setFulfillment('delivery')} data-testid="fulfillment-delivery"
              className={`p-3 rounded-lg border text-sm ${fulfillment === 'delivery' ? 'border-wt-gold bg-wt-gold/10 text-wt-gold' : 'border-wt-border text-wt-text-muted'}`}>
              🚚 Delivery required
            </button>
          </div>
          <CustomerPicker customer={customer} setCustomer={setCustomer} deliveryAddr={fulfillment === 'delivery' ? addr : null} />
          {fulfillment === 'delivery' && (
            <div className="space-y-2 border border-wt-border/50 rounded-lg p-2" data-testid="delivery-address-form">
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="First name *" value={addr.first_name} onChange={(e) => setA('first_name', e.target.value)} data-testid="addr-first-name" />
                <Input placeholder="Last name *" value={addr.last_name} onChange={(e) => setA('last_name', e.target.value)} data-testid="addr-last-name" />
              </div>
              <Input placeholder="Company (optional)" value={addr.company} onChange={(e) => setA('company', e.target.value)} data-testid="addr-company" />
              <AddressAutocomplete value={addr.address1} countryCode={addr.country_code}
                onChange={(v) => setA('address1', v)}
                onPick={(d) => setAddr(a => ({ ...a, address1: d.address1 || a.address1, city: d.city || a.city, province: d.province || a.province, country: d.country || a.country, country_code: d.country_code || a.country_code, zip: d.zip || a.zip }))} />
              <Input placeholder="Address line 2 (optional)" value={addr.address2} onChange={(e) => setA('address2', e.target.value)} data-testid="addr-address2" />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="City *" value={addr.city} onChange={(e) => setA('city', e.target.value)} data-testid="addr-city" />
                <Input placeholder="State / Emirate" value={addr.province} onChange={(e) => setA('province', e.target.value)} data-testid="addr-province" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Input placeholder="Country" value={addr.country} onChange={(e) => setA('country', e.target.value)} data-testid="addr-country" />
                <Input placeholder="ZIP" value={addr.zip} onChange={(e) => setA('zip', e.target.value)} data-testid="addr-zip" />
                <Input placeholder="Phone *" value={addr.phone} onChange={(e) => setA('phone', e.target.value)} data-testid="addr-phone" />
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2" data-testid="payment-methods-row">
            {methods.map((m) => (
              <button key={m.key} onClick={() => addPayment(m)} data-testid={`pay-${m.method}-${m.key}`}
                className="px-3 py-2 rounded-lg border text-sm border-wt-border text-wt-text-muted hover:border-wt-gold hover:text-wt-gold">
                + {m.label}
              </button>
            ))}
            {fulfillment === 'delivery' && (
              <button onClick={() => { setCod(true); setPayments([]); }} data-testid="pay-cod"
                className={`px-3 py-2 rounded-lg border text-sm ${cod ? 'border-wt-gold bg-wt-gold/10 text-wt-gold' : 'border-wt-border text-wt-text-muted'}`}>
                Pay on delivery
              </button>
            )}
          </div>
          {payments.map((p, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`payment-row-${i}`}>
              <span className="text-xs text-wt-text-primary w-24 truncate">{p.label || payLabelUi(p.method)}</span>
              <Input type="number" value={p.amount} onChange={(e) => setP(i, 'amount', e.target.value)} className="h-9 w-24" data-testid={`payment-amount-${i}`} />
              {p.method !== 'cash' && p.method !== 'store_credit' && p.method !== 'link' && (
                <Input placeholder={p.method === 'card' ? 'Terminal ref no *' : 'Reference no (optional)'} value={p.reference} onChange={(e) => setP(i, 'reference', e.target.value)} className="h-9 flex-1" data-testid={`payment-ref-${i}`} />
              )}
              <Button size="sm" variant="ghost" className="text-red-400 h-8 px-2" onClick={() => setPayments((prev) => prev.filter((_, x) => x !== i))} data-testid={`payment-remove-${i}`}>✕</Button>
            </div>
          ))}
          {hasLinkPayment && (
            <p className="text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md p-2" data-testid="link-payment-note">
              A real payment link will be created and copied for the customer — the sale stays <b>unpaid</b> until they pay it.
            </p>
          )}
          {!cod && payments.length > 0 && (
            <div className="text-sm flex justify-between" data-testid="payment-summary">
              <span className="text-wt-text-muted">Paid {formatCurrency(paid)}</span>
              {remaining > 0.005
                ? <span className="text-amber-400">Remaining {formatCurrency(remaining)}</span>
                : change > 0.005
                  ? <span className="text-emerald-400" data-testid="change-label">Change: {formatCurrency(change)}</span>
                  : <span className="text-emerald-400">Fully paid</span>}
            </div>
          )}
          {!cod && payments.length === 0 && <p className="text-[11px] text-wt-text-muted">Tap a payment method to add it — combine several for split payment (e.g. part cash, part card)</p>}
        </div>
        <DialogFooter>
          <Button className="w-full h-12 text-base" disabled={saving || deliveryInvalid || cardMissingRef || (!cod && (payments.length === 0 || remaining > 0.005))} onClick={submit} data-testid="confirm-sale-btn">
            {saving ? 'Processing…' : cardMissingRef ? 'Enter card terminal reference' : `Complete Sale — ${formatCurrency(total)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const payLabelUi = (m) => ({ cash: 'Cash', card: 'Card', link: 'Payment link', store_credit: 'Store credit' }[m] || m);

const CustomerPicker = ({ customer, setCustomer, deliveryAddr }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [nc, setNc] = useState({ first_name: '', last_name: '', phone: '', email: '' });

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/pos/customers?q=${encodeURIComponent(q.trim())}`, auth());
        setResults(r.data.customers || []);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const createCustomer = async () => {
    try {
      const body = { ...nc };
      // If staff already filled the delivery address, save it on the customer too
      if (deliveryAddr && deliveryAddr.address1.trim() && deliveryAddr.city.trim()) {
        body.address = { ...deliveryAddr, first_name: nc.first_name, last_name: nc.last_name, phone: nc.phone };
      }
      const r = await axios.post(`${API}/pos/customers`, body, auth());
      setCustomer(r.data.customer);
      toast.success(r.data.existing ? 'Existing customer selected (same phone)'
        : `Customer created${r.data.shopify?.synced ? ' & synced to Shopify' : ''}`);
      if (!r.data.existing && r.data.shopify && !r.data.shopify.synced && r.data.shopify.error) {
        toast.warning(`Shopify customer sync failed: ${String(r.data.shopify.error).slice(0, 80)}`);
      }
      setShowNew(false); setQ(''); setResults([]);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (customer) {
    return (
      <div className="flex items-center justify-between bg-wt-bg/40 border border-wt-border/50 rounded-lg px-3 py-2" data-testid="customer-chip">
        <span className="text-sm text-wt-text-primary">{customer.name}<span className="text-wt-text-muted text-xs"> · {customer.phone || customer.email || ''}</span></span>
        <button className="text-rose-400 text-xs" onClick={() => setCustomer(null)} data-testid="customer-clear">✕</button>
      </div>
    );
  }
  return (
    <div className="relative space-y-2">
      <div className="flex gap-2">
        <Input placeholder="Search customer — name, email or phone" value={q} onChange={(e) => setQ(e.target.value)} data-testid="customer-search-input" />
        <Button type="button" size="sm" variant="outline" className="border-wt-border h-10 whitespace-nowrap" onClick={() => setShowNew(!showNew)} data-testid="customer-new-btn">+ New</Button>
      </div>
      {results.length > 0 && (
        <div className="absolute z-20 left-0 right-0 top-11 bg-wt-paper border border-wt-border rounded-lg shadow-lg max-h-44 overflow-auto" data-testid="customer-results">
          {results.map(c => (
            <button key={c.id} className="w-full text-left px-3 py-2 text-sm text-wt-text-primary hover:bg-wt-bg/60"
              onClick={() => { setCustomer(c); setQ(''); setResults([]); }} data-testid={`customer-result-${c.id}`}>
              {c.name}<span className="text-wt-text-muted text-xs"> · {c.phone || ''} {c.email ? `· ${c.email}` : ''}</span>
            </button>
          ))}
        </div>
      )}
      {q.trim().length >= 2 && results.length === 0 && !showNew && (
        <p className="text-[11px] text-amber-400/80" data-testid="no-customer-hint">No matching customer — tap <b>+ New</b> to create one (it will sync to Shopify), or continue as a walk-in sale</p>
      )}
      {showNew && (
        <div className="border border-wt-border/50 rounded-lg p-2 space-y-2" data-testid="new-customer-form">
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="First name *" value={nc.first_name} onChange={(e) => setNc({ ...nc, first_name: e.target.value })} data-testid="nc-first-name" />
            <Input placeholder="Last name *" value={nc.last_name} onChange={(e) => setNc({ ...nc, last_name: e.target.value })} data-testid="nc-last-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Phone * (+9715xxxxxxxx)" value={nc.phone} onChange={(e) => setNc({ ...nc, phone: e.target.value })} data-testid="nc-phone" />
            <Input placeholder="Email (optional)" value={nc.email} onChange={(e) => setNc({ ...nc, email: e.target.value })} data-testid="nc-email" />
          </div>
          <Button size="sm" className="w-full" onClick={createCustomer}
            disabled={!nc.first_name.trim() || !nc.last_name.trim() || !nc.phone.trim()} data-testid="nc-create-btn">
            Create Customer (syncs to Shopify)
          </Button>
        </div>
      )}
    </div>
  );
};

const AddressAutocomplete = ({ value, countryCode, onChange, onPick }) => {
  const [preds, setPreds] = useState([]);
  useEffect(() => {
    if ((value || '').trim().length < 3) { setPreds([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/pos/address/autocomplete?q=${encodeURIComponent(value)}${countryCode ? `&country=${countryCode}` : ''}`, auth());
        setPreds(r.data.predictions || []);
      } catch { setPreds([]); }
    }, 350);
    return () => clearTimeout(t);
  }, [value, countryCode]);

  const pick = async (p) => {
    setPreds([]);
    if (p.parsed) { onPick(p.parsed); return; }
    try {
      const r = await axios.get(`${API}/pos/address/place/${p.place_id}`, auth());
      onPick(r.data);
    } catch { onChange(p.description); }
  };

  return (
    <div className="relative">
      <Input placeholder="Address line 1 * (start typing for suggestions)" value={value}
        onChange={(e) => onChange(e.target.value)} data-testid="addr-address1" />
      {preds.length > 0 && (
        <div className="absolute z-20 left-0 right-0 top-11 bg-wt-paper border border-wt-border rounded-lg shadow-lg max-h-40 overflow-auto" data-testid="addr-suggestions">
          {preds.map(p => (
            <button key={p.place_id || p.description} className="w-full text-left px-3 py-2 text-xs text-wt-text-primary hover:bg-wt-bg/60"
              onClick={() => pick(p)} data-testid={`addr-suggestion-${p.place_id || 'geo'}`}>{p.description}</button>
          ))}
        </div>
      )}
    </div>
  );
};

const ReturnsDialog = ({ formatCurrency, registerId, onClose }) => {
  const [orderNumber, setOrderNumber] = useState('');
  const [sale, setSale] = useState(null);
  const [already, setAlready] = useState({});
  const [retQty, setRetQty] = useState({});
  const [reason, setReason] = useState('');
  const [method, setMethod] = useState('cash');
  const [overrideReason, setOverrideReason] = useState('');
  const [saving, setSaving] = useState(false);

  const find = async () => {
    try {
      const r = await axios.get(`${API}/pos/sales/find?order_number=${encodeURIComponent(orderNumber.trim())}`, auth());
      setSale(r.data.order); setAlready(r.data.already_returned || {}); setRetQty({});
      const pms = (r.data.order.pos?.payments || []).map((p) => p.method);
      setMethod(pms.includes('cash') || pms.length === 0 ? 'cash' : 'card');
      setOverrideReason('');
    } catch (e) { toast.error(errMsg(e, 'Sale not found')); setSale(null); }
  };

  const refundTotal = sale ? sale.items.reduce((s, it) => {
    const q = retQty[it.sku] || 0;
    const net = it.quantity ? (it.price * it.quantity - (it.discount || 0)) / it.quantity : 0;
    return s + net * q;
  }, 0) : 0;

  const origMethods = [...new Set((sale?.pos?.payments || []).map((p) => p.method))];
  const methodMismatch = origMethods.length > 0 && !origMethods.includes(method);

  const submit = async () => {
    const items = Object.entries(retQty).filter(([, q]) => q > 0).map(([sku, quantity]) => ({ sku, quantity }));
    if (!items.length) { toast.error('Select quantities to return'); return; }
    if (methodMismatch && !overrideReason.trim()) { toast.error(`Original payment was ${origMethods.join(', ')} — enter an override reason to refund via ${method}`); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/pos/returns`, {
        order_id: sale.id, items, reason, refund_method: method, register_id: registerId,
        override_reason: overrideReason.trim(),
      }, auth());
      toast.success(`Refunded ${formatCurrency(r.data.return.refund_amount)}${r.data.shift_cash_reduced ? ' — deducted from register cash' : ''}${r.data.fully_returned ? ' (full return)' : ''}`);
      onClose();
    } catch (e) { toast.error(errMsg(e, 'Return failed')); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-wt-paper border-wt-border max-w-md" data-testid="returns-dialog">
        <DialogHeader><DialogTitle className="text-wt-text-primary">POS Return / Refund</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          <Input placeholder="Receipt / order number (e.g. POS26090001)" value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && find()}
            data-testid="return-order-input" />
          <Button onClick={find} data-testid="return-find-btn">Find</Button>
        </div>
        {sale && (
          <div className="space-y-2">
            <p className="text-xs text-wt-text-muted">{sale.order_number} · {sale.customer_name} · {formatCurrency(sale.total_amount)} · {sale.payment_status}</p>
            {sale.items.map((it) => {
              const max = it.quantity - (already[it.sku] || 0);
              return (
                <div key={it.sku} className="flex items-center justify-between text-xs bg-wt-bg/40 rounded px-2 py-1.5" data-testid={`return-line-${it.sku}`}>
                  <span className="text-wt-text-primary line-clamp-1">{it.name} <span className="text-wt-text-muted">({max} returnable)</span></span>
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="outline" className="h-7 w-7 border-wt-border" disabled={!max}
                      onClick={() => setRetQty(r => ({ ...r, [it.sku]: Math.max(0, (r[it.sku] || 0) - 1) }))}><Minus className="w-3 h-3" /></Button>
                    <span className="w-6 text-center text-wt-text-primary" data-testid={`return-qty-${it.sku}`}>{retQty[it.sku] || 0}</span>
                    <Button size="icon" variant="outline" className="h-7 w-7 border-wt-border" disabled={!max}
                      onClick={() => setRetQty(r => ({ ...r, [it.sku]: Math.min(max, (r[it.sku] || 0) + 1) }))} data-testid={`return-plus-${it.sku}`}><Plus className="w-3 h-3" /></Button>
                  </div>
                </div>
              );
            })}
            <div className="grid grid-cols-2 gap-2">
              {['cash', 'card'].map(m => (
                <button key={m} onClick={() => setMethod(m)} data-testid={`refund-${m}`}
                  className={`py-2 rounded-lg border text-xs capitalize ${method === m ? 'border-wt-gold bg-wt-gold/10 text-wt-gold' : 'border-wt-border text-wt-text-muted'}`}>
                  Refund {m}{origMethods.includes(m) ? ' (original)' : ''}
                </button>
              ))}
            </div>
            {methodMismatch && (
              <>
                <p className="text-[11px] text-red-400" data-testid="refund-method-mismatch-warning">
                  Original payment was {origMethods.join(', ')} — refunding via {method} requires an override reason
                </p>
                <Input placeholder="Override reason *" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} data-testid="refund-override-reason-input" />
              </>
            )}
            <Input placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="return-reason-input" />
            <Button className="w-full" disabled={saving || refundTotal <= 0} onClick={submit} data-testid="return-confirm-btn">
              {saving ? 'Processing…' : `Refund ${formatCurrency(refundTotal)} & Restock`}
            </Button>
            <p className="text-[10px] text-wt-text-muted">Items are restocked at the store location; cash refunds reduce the open shift's expected cash; Shopify order gets a return note.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const SetupDialog = ({ stores, onRefresh, onClose }) => {
  const [store, setStore] = useState({ name: '', address: '', trn: '', receipt_footer: '', industry: 'retail' });
  const [regName, setRegName] = useState('');
  const [regStore, setRegStore] = useState('');

  const addStore = async () => {
    try {
      const r = await axios.post(`${API}/pos/stores`, store, auth());
      toast.success('Store created — now add a register for it');
      setStore({ name: '', address: '', trn: '', receipt_footer: '', industry: 'retail' });
      setRegStore(r.data.id);
      onRefresh();
    } catch (e) { toast.error(errMsg(e, 'Failed')); }
  };
  const addRegister = async () => {
    try {
      await axios.post(`${API}/pos/registers`, { store_id: regStore, name: regName }, auth());
      toast.success('Register created'); setRegName(''); onRefresh();
    } catch (e) { toast.error(errMsg(e, 'Failed')); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-wt-paper border-wt-border max-w-md" data-testid="pos-setup-dialog">
        <DialogHeader><DialogTitle className="text-wt-text-primary">POS Setup</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <p className="text-xs font-medium text-wt-text-primary">New store</p>
          <Input placeholder="Store name *" value={store.name} onChange={(e) => setStore({ ...store, name: e.target.value })} data-testid="setup-store-name" />
          <Input placeholder="Address" value={store.address} onChange={(e) => setStore({ ...store, address: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="TRN (for receipt)" value={store.trn} onChange={(e) => setStore({ ...store, trn: e.target.value })} />
            <Input placeholder="Receipt footer" value={store.receipt_footer} onChange={(e) => setStore({ ...store, receipt_footer: e.target.value })} />
          </div>
          <select value={store.industry} onChange={(e) => setStore({ ...store, industry: e.target.value })}
            className="w-full bg-wt-bg border border-wt-border rounded px-2 py-2 text-sm text-wt-text-primary" data-testid="setup-industry-select">
            <option value="retail">Retail (furniture, garments, general)</option>
            <option value="grocery">Grocery (barcode-first, fast scanning)</option>
            <option value="restaurant">Restaurant (dine-in tables, takeaway, KOT)</option>
          </select>
          <Button size="sm" onClick={addStore} disabled={!store.name.trim()} data-testid="setup-add-store-btn">Add Store</Button>
          <div className="border-t border-wt-border/50 pt-2 mt-2">
            <p className="text-xs font-medium text-wt-text-primary mb-2">New register</p>
            <div className="flex gap-2">
              <select value={regStore} onChange={(e) => setRegStore(e.target.value)} className="bg-wt-bg border border-wt-border rounded px-2 text-sm text-wt-text-primary flex-1" data-testid="setup-register-store">
                <option value="">Store…</option>
                {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <Input placeholder="Register name" value={regName} onChange={(e) => setRegName(e.target.value)} data-testid="setup-register-name" />
              <Button size="sm" onClick={addRegister} disabled={!regStore || !regName.trim()} data-testid="setup-add-register-btn">Add</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
